import { HeroSection } from "@/components/academician/hero-section"
import { FeaturesSection } from "@/components/academician/features-section"
import { HowItWorksSection } from "@/components/academician/how-it-works-section"
import { CohortsSection } from "@/components/academician/cohorts-section"
import { TestimonialsSection } from "@/components/academician/testimonials-section"
import { Footer } from "@/components/academician/footer"
import { useNavigate } from "react-router-dom"
import { LogInIcon } from "lucide-react"

interface CohortData {
  name: string;
  status: string;
  startDate: string;
  duration: string;
  price: string;
  enrolled: number;
  capacity: number;
  bannerImage?: string;
  href?: string;
}

interface LandingPageProps {
  /**
   * Cohorts data from the server
   */
  cohorts?: CohortData[];
  /**
   * Additional CSS classes
   */
  className?: string;
}

export function LandingPage({ cohorts = [], className = "" }: LandingPageProps) {
  const navigate = useNavigate()

  const handleViewCohorts = () => {
    navigate("/cohorts")
  }

  const handleLearnMore = () => {
    // Smooth scroll to features section
    const featuresSection = document.getElementById("features")
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <div className={`min-h-screen bg-background ${className}`}>
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="inline-flex items-center gap-2">
              <img src="/logo.png" alt="Academician Startup" className="w-8 h-8 rounded-lg" />
              <span className="text-xl font-bold text-[#666666]">Academician Startup</span>
            </a>
            <div className="flex items-center gap-3">
              <a
                href="/users/sign_in"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                <LogInIcon className="w-4 h-4" />
                Login
              </a>
              <a
                href="/users/sign_up"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-md transition-colors"
              >
                Sign Up
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <HeroSection
        onPrimaryCtaClick={handleViewCohorts}
        onSecondaryCtaClick={handleLearnMore}
      />

      {/* Features Section */}
      <div id="features">
        <FeaturesSection />
      </div>

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Current Cohorts Section */}
      <CohortsSection
        cohorts={cohorts}
        viewAllHref="/cohorts"
      />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Footer */}
      <Footer />
    </div>
  )
}
